import type { BlendMode } from '../core/types';

/**
 * A brush tip is either generated from a circle or sampled from an image, which
 * is the same split Photopea documents: "Circular" tips have a hardness, while
 * "Pattern" tips are a raster image that pixelates when scaled far past its own
 * resolution.
 */
export type BrushTipKind = 'circular' | 'pattern';

/** Grayscale coverage of a sampled tip, one byte per pixel, row 0 at the top. */
export interface TipImage {
  id: string;
  name: string;
  width: number;
  height: number;
  data: Uint8Array;
}

export interface BrushTip {
  kind: BrushTipKind;
  /** 0 = fully soft falloff, 1 = hard edge. Circular tips only. */
  hardness: number;
  /** Rotation of the tip in degrees. */
  angle: number;
  /** 0.05..1, squeezes the tip along its minor axis. */
  roundness: number;
  /** Distance between stamps as a fraction of the tip diameter. */
  spacing: number;
  /** Tip image id for `pattern` tips; ignored by circular ones. */
  imageId: string | null;
}

/**
 * How a stamp deviates from the static tip. Every field is 0..1 and scales a
 * fixed maximum, so presets stay readable and interpolate sensibly.
 */
export interface BrushDynamics {
  sizeJitter: number;
  angleJitter: number;
  roundnessJitter: number;
  /** Sideways offset of each stamp, as a fraction of the tip diameter. */
  scatter: number;
  flowJitter: number;
  /** Rotates every stamp to follow the direction of the stroke. */
  followDirection: boolean;
  pressureSize: boolean;
  pressureOpacity: boolean;
}

/** Gallery grouping for the Brush panel and tool context menu. */
export type BrushPresetCategory =
  | 'Basic'
  | 'Pencils'
  | 'Dry Media'
  | 'Wet Media'
  | 'Markers'
  | 'Ink & Calligraphy'
  | 'Airbrush & Spray'
  | 'Texture & FX';

export interface BrushPreset {
  id: string;
  name: string;
  /** Tip diameter in document pixels. */
  size: number;
  tip: BrushTip;
  dynamics: BrushDynamics;
  /** Optional gallery section; omitted for user / ABR imports. */
  category?: BrushPresetCategory;
}

/** Everything a paint tool needs from the store to build a stroke. */
export interface PaintOptions {
  size: number;
  hardness: number;
  /** 0..1, the ceiling a single stroke may reach. */
  opacity: number;
  /** 0..1, coverage laid down by one stamp. */
  flow: number;
  spacing: number;
  angle: number;
  roundness: number;
  /** Tip image id, or null for a generated circular tip. */
  tipImageId: string | null;
  blendMode: BlendMode;
  /** 0 = raw pointer positions, 0.9 = maximum lag. */
  smoothing: number;
  /** Preset the options were last loaded from, for highlighting the gallery. */
  presetId: string | null;
  sizeJitter: number;
  angleJitter: number;
  roundnessJitter: number;
  scatter: number;
  flowJitter: number;
  followDirection: boolean;
  pressureSize: boolean;
  pressureOpacity: boolean;
  /** Strength of Blur, Sharpen and Smudge, 0..1. */
  strength: number;
  /** Exposure of Dodge and Burn, 0..1. */
  exposure: number;
  /** Tonal range Dodge and Burn act on. */
  range: 'shadows' | 'midtones' | 'highlights';
  /** Sponge direction. */
  sponge: 'saturate' | 'desaturate';
  /** Clone Stamp keeps the source offset across strokes. */
  aligned: boolean;
  /** Clone and healing sample the composite instead of the active layer. */
  sampleAllLayers: boolean;
}

export const DEFAULT_DYNAMICS: BrushDynamics = {
  sizeJitter: 0,
  angleJitter: 0,
  roundnessJitter: 0,
  scatter: 0,
  flowJitter: 0,
  followDirection: false,
  pressureSize: true,
  pressureOpacity: false,
};

export function tipFromOptions(options: PaintOptions): BrushTip {
  return {
    kind: options.tipImageId ? 'pattern' : 'circular',
    hardness: options.hardness,
    angle: options.angle,
    roundness: options.roundness,
    spacing: options.spacing,
    imageId: options.tipImageId,
  };
}

export function dynamicsFromOptions(options: PaintOptions): BrushDynamics {
  return {
    sizeJitter: options.sizeJitter,
    angleJitter: options.angleJitter,
    roundnessJitter: options.roundnessJitter,
    scatter: options.scatter,
    flowJitter: options.flowJitter,
    followDirection: options.followDirection,
    pressureSize: options.pressureSize,
    pressureOpacity: options.pressureOpacity,
  };
}

/** Applies a gallery preset to a tool's options, leaving tool-specific fields. */
export function applyPreset(options: PaintOptions, preset: BrushPreset): PaintOptions {
  return {
    ...options,
    size: preset.size,
    hardness: preset.tip.hardness,
    angle: preset.tip.angle,
    roundness: preset.tip.roundness,
    spacing: preset.tip.spacing,
    tipImageId: preset.tip.imageId,
    presetId: preset.id,
    sizeJitter: preset.dynamics.sizeJitter,
    angleJitter: preset.dynamics.angleJitter,
    roundnessJitter: preset.dynamics.roundnessJitter,
    scatter: preset.dynamics.scatter,
    flowJitter: preset.dynamics.flowJitter,
    followDirection: preset.dynamics.followDirection,
    pressureSize: preset.dynamics.pressureSize,
    pressureOpacity: preset.dynamics.pressureOpacity,
  };
}

export function presetFromOptions(id: string, name: string, options: PaintOptions): BrushPreset {
  return {
    id,
    name,
    size: options.size,
    tip: tipFromOptions(options),
    dynamics: dynamicsFromOptions(options),
    category: undefined,
  };
}
